import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { updateCompany } from "@/api/companies";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { RichTextRenderer } from "@/components/ui/RichTextRenderer";
import { cn } from "@/lib/utils";

const SAVED_BADGE_HOLD_MS = 2000;

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't save notes.";
}

interface NotesEditorProps {
  projectSlug: string;
  companyId: number;
  initialNotes: string | null;
  onSaved: (newNotes: string | null) => void;
  /** Read-only mode — render the notes via the sanitising renderer
   *  with no toolbar or click target. */
  editable?: boolean;
}

/** Imperative entry-point for the company toolbar's "Note" button —
 *  switches the section into edit mode immediately, mirroring what
 *  would happen on a click of the click-to-add affordance. */
export interface NotesEditorHandle {
  startEdit: () => void;
}

type Mode = "collapsed" | "editing" | "reading";
type SaveStatus = "idle" | "saving" | "saved" | "error";

export const NotesEditor = forwardRef<NotesEditorHandle, NotesEditorProps>(
  function NotesEditor(
    { projectSlug, companyId, initialNotes, onSaved, editable = true },
    ref,
  ) {
    // The persisted (server-confirmed) HTML — drives the read-mode
    // surface and the "is this empty?" checks. Updated optimistically
    // on save success.
    const [savedNotes, setSavedNotes] = useState<string>(initialNotes ?? "");
    // The current draft inside the editor. Diverges from savedNotes
    // while editing; reset to it on Cancel; copied to it on Save.
    const [draft, setDraft] = useState<string>(initialNotes ?? "");
    const [hasChanged, setHasChanged] = useState(false);
    const [editing, setEditing] = useState(false);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

    const lastSavedRef = useRef<string>(initialNotes ?? "");
    const savedBadgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    // Token guards against stale responses overwriting newer state.
    const inflightTokenRef = useRef(0);

    // External resets — switching companies, parent refresh, etc.
    useEffect(() => {
      const next = initialNotes ?? "";
      setSavedNotes(next);
      setDraft(next);
      lastSavedRef.current = next;
      setSaveStatus("idle");
      setEditing(false);
      setHasChanged(false);
    }, [initialNotes]);

    useImperativeHandle(ref, () => ({
      startEdit: () => {
        if (!editable) return;
        setDraft(savedNotes);
        setHasChanged(false);
        setEditing(true);
      },
    }));

    const persist = useCallback(
      async (next: string): Promise<boolean> => {
        const payload = next.trim() ? next : null;
        if ((payload ?? "") === lastSavedRef.current) return true;
        const token = ++inflightTokenRef.current;
        setSaveStatus("saving");
        try {
          await updateCompany(projectSlug, companyId, { notes: payload });
          if (token !== inflightTokenRef.current) return true; // superseded
          lastSavedRef.current = payload ?? "";
          setSavedNotes(payload ?? "");
          onSaved(payload);
          setSaveStatus("saved");
          if (savedBadgeTimerRef.current) {
            clearTimeout(savedBadgeTimerRef.current);
          }
          savedBadgeTimerRef.current = setTimeout(() => {
            if (token === inflightTokenRef.current) setSaveStatus("idle");
          }, SAVED_BADGE_HOLD_MS);
          return true;
        } catch (err) {
          if (token !== inflightTokenRef.current) return false;
          setSaveStatus("error");
          toast.error(extractErrorMessage(err));
          return false;
        }
      },
      [projectSlug, companyId, onSaved],
    );

    function handleChange(html: string) {
      setDraft(html);
      // Track diffs against the *server* state, not against the
      // initial draft, so re-typing a string identical to what's
      // already saved doesn't enable Save.
      setHasChanged(html !== lastSavedRef.current);
      // The previous "Saved ✓" badge stays misleading once the user
      // resumes typing — flip back to idle so the next save shows the
      // confirmation freshly.
      if (saveStatus === "saved") setSaveStatus("idle");
    }

    async function handleSave() {
      if (!hasChanged) return;
      const ok = await persist(draft);
      if (ok) {
        setEditing(false);
        setHasChanged(false);
      }
    }

    function handleCancel() {
      setDraft(savedNotes);
      setHasChanged(false);
      setEditing(false);
    }

    // Cleanup timers on unmount.
    useEffect(() => {
      return () => {
        if (savedBadgeTimerRef.current) {
          clearTimeout(savedBadgeTimerRef.current);
        }
      };
    }, []);

    const hasContent = savedNotes.trim().length > 0;
    const mode: Mode = !editable
      ? "reading"
      : editing
        ? "editing"
        : hasContent
          ? "reading"
          : "collapsed";

    if (mode === "reading" && !editable) {
      if (!hasContent) {
        return (
          <p className="px-3 py-2 text-sm italic text-slate-400">
            No notes yet.
          </p>
        );
      }
      return <RichTextRenderer content={savedNotes} className="px-3 py-2" />;
    }

    return (
      // The id sits on the wrapper (not the section header in
      // CompanyDetail) so the toolbar's "Note" button can scroll the
      // editor itself into view — the section header is above the
      // textarea, and centering on it would still leave the editor
      // half-clipped at the bottom of the viewport.
      <div id="company-notes-editor" className="relative">
        <AnimatePresence mode="wait" initial={false}>
          {mode === "collapsed" && (
            <motion.button
              key="collapsed"
              type="button"
              onClick={() => {
                setDraft("");
                setHasChanged(false);
                setEditing(true);
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className={cn(
                "flex w-full items-center gap-2 rounded-xl border border-dashed border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-400 transition-colors",
                "hover:border-indigo-300 hover:bg-indigo-50/30 hover:text-indigo-600",
              )}
            >
              <Plus className="h-4 w-4" />
              Add a note…
            </motion.button>
          )}

          {mode === "editing" && (
            <motion.div
              key="editing"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              style={{ overflow: "hidden" }}
              className="overflow-hidden rounded-xl border border-indigo-200 bg-white shadow-card-rest"
            >
              {/* No onBlur close — the editor stays open until Save
               *  or Cancel. A misclick outside no longer drops the
               *  user's draft. We also pass status="idle" so the
               *  editor's built-in badge stays silent; the explicit
               *  action bar below carries the save indicator. */}
              <RichTextEditor
                content={draft}
                onChange={handleChange}
                autoFocus
                placeholder="Write your notes…"
              />
              <div className="flex items-center justify-end gap-2 border-t border-slate-900/[0.06] bg-slate-50/60 px-3 py-2">
                <SaveStatusBadge status={saveStatus} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  disabled={saveStatus === "saving"}
                  className="h-7 px-2 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void handleSave()}
                  disabled={!hasChanged || saveStatus === "saving"}
                  className="h-7 px-3 text-xs"
                >
                  {saveStatus === "saving" && (
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  Save
                </Button>
              </div>
            </motion.div>
          )}

          {mode === "reading" && editable && (
            <motion.button
              key="reading"
              type="button"
              onClick={(e) => {
                // Don't switch to edit mode when the user clicks a
                // link inside the rendered notes — that's a navigation
                // intent, not an edit one.
                if ((e.target as HTMLElement).closest("a")) return;
                setDraft(savedNotes);
                setHasChanged(false);
                setEditing(true);
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className={cn(
                "block w-full cursor-text rounded-xl bg-white px-4 py-3 text-left shadow-card-rest transition-[box-shadow] duration-150 motion-reduce:transition-none",
                "hover:shadow-plane-lift",
              )}
              style={{ minHeight: 60 }}
            >
              <RichTextRenderer content={savedNotes} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

function SaveStatusBadge({ status }: { status: SaveStatus }) {
  return (
    <AnimatePresence>
      {status === "saving" && (
        <motion.span
          key="saving"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="mr-auto text-[11px] font-medium text-slate-400"
        >
          Saving…
        </motion.span>
      )}
      {status === "saved" && (
        <motion.span
          key="saved"
          initial={{ opacity: 0, y: 2 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="mr-auto text-[11px] font-medium text-emerald-600"
        >
          Saved ✓
        </motion.span>
      )}
      {status === "error" && (
        <motion.span
          key="error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="mr-auto text-[11px] font-medium text-rose-600"
        >
          Save failed
        </motion.span>
      )}
    </AnimatePresence>
  );
}
